import { DEFAULT_HOTEL_TAB_ROUTING, DEFAULT_HOTEL_TABS } from "@/constants";
import {
  JapanHotel,
  TabDatasource,
  GetHotelTabQuery,
  GetHotelDataFilterTabQuery,
  GetHotelTemplateQuery,
  ContentJapanHotelTab,
  Enum_Componentmoleculeshoteltabitem_Use_Existing_Tab,
} from "@/types/generated";
import { isHotelDetailSubPage } from "@/utils/queryUtils";
import {
  generateLinkFromGeneralLink,
  generateTargetFromLink,
  LinkConfig,
} from "@/lib/utils";

export interface INavLink {
  title: string;
  item_name: string;
  url: string;
  type: "anchor" | "";
  isActive: boolean;
  linkType: "external" | "internal";
}

/** URL prefixes for CMS custom tabs — mirrors CustomTabsNav EXISTING_TAB_URL_MAP */
export const EXISTING_TAB_URL_MAP: Record<
  Enum_Componentmoleculeshoteltabitem_Use_Existing_Tab,
  string
> = {
  overview: "",
  access: "location-",
  attractions: "location-",
  banquet: "banquets-",
  breakfast: "breakfast-",
  facilities: "facilities-",
  faq: "faq-",
  onsen: "onsen-",
  restaurant: "restaurants-",
  rooms: "rooms-",
  gallery: "gallery-",
  activity: "activity-",
};

const isTabActive = (
  tab: TabDatasource,
  lastPathSegment: string,
  hotelSlug: string,
  sortedTabs: TabDatasource[],
  fullPathname?: string,
): boolean => {
  const isFirstTabAndHotelDetail =
    tab.item_name === sortedTabs[0]?.item_name &&
    lastPathSegment.startsWith("hotel-");

  const isBanquetsAndMeetings =
    lastPathSegment.startsWith("banquets") && tab.item_name === "Meetings";

  // Banquet detail page (/banquets-hotel-xxx/banquet-slug) → Meetings tab active
  const pathSegments = (fullPathname || "").split("/").filter(Boolean);
  const lastTwoSegments = pathSegments.slice(-2);
  const isBanquetDetailAndMeetings =
    lastTwoSegments[0]?.startsWith("banquets-") &&
    !!lastTwoSegments[1] &&
    tab.item_name === "Meetings";

  // Party plan detail page (/brand-hotel-xxx/party-slug) → Meetings tab active
  const isPartyPlanAndMeetings =
    lastPathSegment.startsWith("party-") && tab.item_name === "Meetings";

  const isRestaurantDetailAndRestaurants =
    lastTwoSegments[0]?.startsWith("restaurants-") &&
    !!lastTwoSegments[1] &&
    tab.item_name === "Restaurants";

  // Handle attractions tab - only active for attraction- pattern
  const isAttractionsTab =
    tab.item_name === "Attractions" &&
    lastPathSegment.startsWith("attraction-");

  // Handle location/access tab - only active for location- pattern
  const isLocationTab =
    tab.item_name === "Location" && lastPathSegment.startsWith("location-");

  // Handle pool-spa tab - active when URL starts with pool-spa-
  const isPoolSpaTab =
    tab.item_name === "Onsen" && lastPathSegment.startsWith("pool-spa-");

  // Handle gallery tab - active when URL starts with gallery-
  const isGalleryTab =
    tab.item_name === "gallery" && lastPathSegment.startsWith("gallery-");

  const isPathMatchingTab =
    lastPathSegment.toLowerCase().split("-")[0] ===
    tab.item_name?.toLowerCase();

  return (
    isFirstTabAndHotelDetail ||
    isBanquetsAndMeetings ||
    isBanquetDetailAndMeetings ||
    isPartyPlanAndMeetings ||
    isRestaurantDetailAndRestaurants ||
    isAttractionsTab ||
    isLocationTab ||
    isPoolSpaTab ||
    isGalleryTab ||
    isPathMatchingTab
  );
};

// Helper: Extract all datasources from hotel data
export const extractHotelDatasources = (dataFilterTab: JapanHotel) => ({
  meeting_rooms: dataFilterTab?.meeting_rooms || [],
  hotel_restaurants_datasource:
    dataFilterTab?.datasources?.find(
      (item) => !!item?.hotel_restaurants_datasources,
    )?.hotel_restaurants_datasources?.[0]?.restaurants || [],
  hotel_restaurants: dataFilterTab?.hotel_restaurants || [],
  breakfast: dataFilterTab?.breakfast || null,
  weddingData: dataFilterTab?.wedding || null,
  onsen_features: dataFilterTab?.onsen_features || null,
  hotel_onsen_datasource:
    dataFilterTab?.datasources?.[0]?.onsen_feature_datasource?.[0]
      ?.onsen_details?.onsen_features || [],
  hotel_facilities: dataFilterTab?.hotel_facilities || null,
  hotel_facilities_datasource:
    dataFilterTab?.datasources?.[0]?.hotel_facilities_datasources?.[0]
      ?.facilities_list || [],
  hotel_meeting_rooms_datasource:
    dataFilterTab?.datasources?.[0]?.meeting_rooms_datasources?.[0]?.information
      ?.meeting_room_list || [],
  service_apartments: dataFilterTab?.service_apartment || null,
  guest_room: dataFilterTab?.hotel_rooms || null,
  hotel_faqs: dataFilterTab?.hotel_faqs || null,
  hotel_party_plan_layouts: dataFilterTab?.custom_page_Layouts || [],
  activity_details: dataFilterTab?.activity_details || null,
  slug: dataFilterTab?.slug,
  activity_datasource:dataFilterTab?.datasources?.[0]?.hotel_activity_datasources?.[0] || null,
  gallery_datasource: dataFilterTab?.datasources?.[0]?.hotel_gallery_datasources?.[0] || null,
});

// Helper: Build content availability map
export const buildContentMap = (
  datasources: ReturnType<typeof extractHotelDatasources>,
  tabNames: Record<string, string>,
) => ({
  [tabNames.meetings]:
    !!datasources.meeting_rooms?.length &&
    !!datasources.hotel_meeting_rooms_datasource?.length,
  [tabNames.rooms]: !!datasources.guest_room?.length,
  [tabNames.restaurants]:
    !!datasources.hotel_restaurants?.length &&
    !!datasources.hotel_restaurants_datasource?.length,
  [tabNames.weddings]: !!datasources.weddingData?.wedding_website,
  [tabNames.breakfast]: !!datasources.breakfast?.documentId,
  [tabNames.onsen]:
    !!datasources.onsen_features?.length &&
    !!datasources.hotel_onsen_datasource?.length,
  [tabNames.facilities]:
    !!datasources.hotel_facilities?.hotel_facility?.length &&
    !!datasources.hotel_facilities_datasource?.length,
  [tabNames.faq]: !!datasources.hotel_faqs?.documentId,
  [tabNames.service_apartment]:
    !!datasources.service_apartments?.service_apartment_link,
  [tabNames.activity]: !!datasources.activity_details || !!datasources.activity_datasource?.template_data?.template?.item_name,
  [tabNames.gallery]: !!datasources.gallery_datasource?.item_name,
});

// Helper: Generate tab link based on tab type
const generateTabLink = (
  tabTitle: string,
  slug: string | null | undefined,
  queryString: string | null,
  weddingUrl: string | undefined,
  attractionsTabName: string,
  weddingsTabName: string,
  isAttractionTemplate?: boolean,
): string => {
  if (tabTitle === attractionsTabName) {
    let link = isAttractionTemplate ? `attraction-${slug}` : `location-${slug}`;
    if (queryString) link += queryString;
    link += isAttractionTemplate ? "" : "#attractions";
    return link;
  }
  if (tabTitle === weddingsTabName) {
    return weddingUrl || "";
  }
  return "";
};

// Helper: Get tab route prefix
const getTabRoutePrefix = (tabTitle: string): string | undefined => {
  return DEFAULT_HOTEL_TAB_ROUTING.find((t) => t.title === tabTitle)?.url;
};

// Helper: Check if tab should be included in menu
const shouldIncludeTab = (
  tabTitle: string,
  hasContentMap: Record<string, boolean>,
): boolean => {
  return !Object.hasOwn(hasContentMap, tabTitle) || hasContentMap[tabTitle];
};

// Helper: Calculate service apartment active state
const calculateServiceApartmentActiveState = (
  lastPathSegment: string,
  currentPathWithoutHash: string,
  hotelSlug: string,
  desiredPath: string,
): boolean => {
  if (!lastPathSegment || !desiredPath) return false;

  const lowerLastPath = lastPathSegment.toLowerCase();
  const lowerHotelSlug = hotelSlug.toLowerCase();
  return (
    currentPathWithoutHash.endsWith(desiredPath) ||
    (lowerLastPath === "serviceapartment" &&
      currentPathWithoutHash.includes(lowerHotelSlug))
  );
};

// Helper: Determine tab link based on tab type
const determineLinkByTabType = (
  tabTitle: string,
  datasources: ReturnType<typeof extractHotelDatasources>,
  queryString: string | null,
  tabNames: Record<string, string>,
  isAttractionTemplate?: boolean,
): string => {
  if (tabTitle === tabNames.attractions) {
    return generateTabLink(
      tabTitle,
      datasources.slug,
      queryString,
      undefined,
      tabNames.attractions,
      tabNames.weddings,
      isAttractionTemplate,
    );
  }
  if (tabTitle === tabNames.weddings) {
    return generateTabLink(
      tabTitle,
      datasources.slug,
      queryString,
      datasources.weddingData?.wedding_website?.metadata?.url,
      tabNames.attractions,
      tabNames.weddings,
    );
  }
  const tabRoutePrefix = getTabRoutePrefix(tabTitle);
  return `${tabRoutePrefix}${datasources.slug}`;
};

const POOL_AND_SPA_HOTEL_SLUG = "hotel-mystays-premier-narita-chiba";

// Helper: Create nav link for a tab
const createNavLink = (
  tab: TabDatasource,
  tabTitle: string,
  tabConfig: {
    datasources: ReturnType<typeof extractHotelDatasources>;
    queryString: string | null;
    tabNames: Record<string, string>;
    hotelSlug: string;
    lastPathSegment: string;
    fullPathname?: string;
    sortedTabs: TabDatasource[];
    isAttractionTemplate?: boolean;
  },
): INavLink => {
  const rawTabLink = determineLinkByTabType(
    tabTitle,
    tabConfig.datasources,
    tabConfig.queryString,
    tabConfig.tabNames,
    tabConfig.isAttractionTemplate,
  );

  const isPoolAndSpaHotel =
    tab.item_name === DEFAULT_HOTEL_TABS.onsen &&
    tabConfig.hotelSlug === POOL_AND_SPA_HOTEL_SLUG;

  const tabLink = isPoolAndSpaHotel
    ? `pool-spa-${tabConfig.datasources.slug}`
    : rawTabLink;

  const isAbsoluteUrl = /^https?:\/\//.test(tabLink);

  const tabDisplayTitle = isPoolAndSpaHotel
    ? "プール＆スパ"
    : (tab.tab_title as string);

  return {
    title: tabDisplayTitle,
    item_name: tab.item_name,
    url: isAbsoluteUrl ? tabLink : `/${tabLink}`,
    type:
      tabTitle === tabConfig.tabNames.attractions &&
      !tabConfig.isAttractionTemplate
        ? "anchor"
        : "",
    isActive: isTabActive(
      tab,
      tabConfig.lastPathSegment,
      tabConfig.hotelSlug,
      tabConfig.sortedTabs,
      tabConfig.fullPathname,
    ),
    linkType:
      tabTitle === tabConfig.tabNames.weddings ? "external" : "internal",
  };
};

export const createTabList = (
  hotelTab: TabDatasource[],
  dataFilterTab: JapanHotel,
  currentPathWithoutHash: string,
  hash: string,
  hotelSlug: string,
  locale: string,
  queryString: string | null,
  isAttractionTemplate?: boolean,
): INavLink[] => {
  const menu: INavLink[] = [];
  const lastPathSegment = hash
    ? hash.replace("#", "")
    : currentPathWithoutHash.split("/").pop() || "";

  // Extract all datasources
  const datasources = extractHotelDatasources(dataFilterTab);

  const tabNames = {
    meetings: DEFAULT_HOTEL_TABS.meetings,
    weddings: DEFAULT_HOTEL_TABS.weddings,
    restaurants: DEFAULT_HOTEL_TABS.restaurants,
    breakfast: DEFAULT_HOTEL_TABS.breakfast,
    onsen: DEFAULT_HOTEL_TABS.onsen,
    facilities: DEFAULT_HOTEL_TABS.facilities,
    faq: DEFAULT_HOTEL_TABS.faq,
    location: DEFAULT_HOTEL_TABS.location,
    attractions: DEFAULT_HOTEL_TABS.attractions,
    service_apartment: DEFAULT_HOTEL_TABS.service_apartment,
    rooms: DEFAULT_HOTEL_TABS.rooms,
    activity: DEFAULT_HOTEL_TABS.activity,
    gallery: DEFAULT_HOTEL_TABS.gallery,
  };

  const tabOrder = Object.values(DEFAULT_HOTEL_TABS);
  const sortedTabs = [...hotelTab].sort(
    (a, b) => tabOrder.indexOf(a.item_name) - tabOrder.indexOf(b.item_name),
  );

  // Build content map
  const hasContentMap = buildContentMap(datasources, tabNames);

  // Process each tab
  for (const tab of sortedTabs) {
    const tabTitle = tab.item_name;

    // Skip if no content
    if (!shouldIncludeTab(tabTitle, hasContentMap)) {
      continue;
    }

    // Create and add nav link
    const navLink = createNavLink(tab, tabTitle, {
      datasources,
      queryString,
      tabNames,
      hotelSlug,
      lastPathSegment,
      fullPathname: currentPathWithoutHash,
      sortedTabs,
      isAttractionTemplate,
    });
    menu.push(navLink);
  }

  // Handle service apartment tab
  const serviceApartmentLinkFromData =
    datasources.service_apartments?.service_apartment_link;
  const serviceApartmentTitle =
    datasources.service_apartments?.service_apartment_title ||
    DEFAULT_HOTEL_TABS.service_apartment;

  const saTabAlreadyInMenu = menu.some(
    (navLink) => navLink.item_name === DEFAULT_HOTEL_TABS.service_apartment,
  );

  if (serviceApartmentLinkFromData && !saTabAlreadyInMenu) {
    const formattedHotelSlugForURL = hotelSlug.startsWith("hotel-")
      ? hotelSlug
      : `hotel-${hotelSlug}`;
    const desiredServiceApartmentPath = `${formattedHotelSlugForURL}/serviceapartment`;

    const isManuallyAddedSAActive = calculateServiceApartmentActiveState(
      lastPathSegment,
      currentPathWithoutHash,
      hotelSlug,
      desiredServiceApartmentPath,
    );

    menu.push({
      title: serviceApartmentTitle,
      item_name: DEFAULT_HOTEL_TABS.service_apartment,
      url: desiredServiceApartmentPath,
      type: "",
      isActive: isManuallyAddedSAActive,
      linkType: "internal",
    });
  }

  // Add dynamic party plan layout tabs
  const partyPlanLayouts = datasources.hotel_party_plan_layouts || [];
  for (const layout of partyPlanLayouts) {
    if (!layout) continue;

    // Only show tab if show is explicitly true
    if (layout.show !== true) continue;

    const urlPrefix = layout.slug ? `${layout.slug}-` : "";
    const layoutSlug = layout.slug || "";
    const tabTitle =
      layout.tab_title ||
      layout.display_name ||
      layout.item_name ||
      "Dynamic Tab";

    if (urlPrefix && layoutSlug && datasources.slug) {
      const dynamicTabUrl = `${urlPrefix}${datasources.slug}`;
      const isDynamicTabActive = lastPathSegment === dynamicTabUrl;

      menu.push({
        title: tabTitle,
        item_name: layout.item_name || layoutSlug,
        url: dynamicTabUrl,
        type: "",
        isActive: isDynamicTabActive,
        linkType: "internal",
      });
    }
  }

  return menu;
};

export const getTemplateNavLinks = (
  hotelTab: GetHotelTabQuery | null | undefined,
  dataFilterTab: GetHotelDataFilterTabQuery | null | undefined,
  hotelTemplates: GetHotelTemplateQuery | null | undefined,
  pathName: string | undefined,
  locale: string,
  isAttractionTemplate?: boolean
): INavLink[] | null => {
  let navLinks: INavLink[] | null = null;

  if (hotelTab && dataFilterTab && pathName) {
    let hotelTabData = ((hotelTab?.sharedDatasources || []).find(
      (t) => !!t?.hotel,
    )?.hotel?.tab_datasources || []) as TabDatasource[];

    const templateTabs = (hotelTemplates?.japanHotels?.[0] as JapanHotel)
      ?.datasources?.[0]?.hotel_menu_datasource?.tabs;

    if (
      templateTabs &&
      Array.isArray(templateTabs) &&
      templateTabs.length > 0
    ) {
      const tabOrderMap = new Map(
        templateTabs.map((tab) => [tab.documentId, Number(tab.order) || 0]),
      );

      const filteredTabs = hotelTabData.filter((tab) =>
        tabOrderMap.has(tab.documentId),
      );

      hotelTabData = filteredTabs.sort((a, b) => {
        const orderA = tabOrderMap.get(a.documentId) ?? Infinity;
        const orderB = tabOrderMap.get(b.documentId) ?? Infinity;
        return orderA - orderB;
      });
    }

    const hotelData = dataFilterTab?.japanHotels?.[0] as JapanHotel;

    if (hotelData && hotelTabData.length > 0) {
      const pathSegments = pathName.split("/").filter(Boolean);
      const lastSegment = isHotelDetailSubPage(pathName) ? pathSegments[pathSegments.length - 2] : pathSegments[pathSegments.length - 1] || "";
      // const isAttractionTemplate = lastSegment.startsWith("attraction-");
      let hotelSlug = lastSegment;
      if (lastSegment.includes("hotel-")) {
        const hotelIndex = lastSegment.indexOf("hotel-");
        hotelSlug = lastSegment.substring(hotelIndex);
      }

      navLinks = createTabList(
        hotelTabData,
        hotelData,
        pathName,
        "",
        hotelSlug,
        locale,
        null,
        isAttractionTemplate,
      );

      if (
        (hotelTemplates?.japanHotels?.[0] as JapanHotel)?.datasources?.[0]
          ?.hotel_menu_datasource?.showTestimonials
      ) {
        navLinks.push({
          title: "お客様の声",
          item_name: "testimonials",
          url: `/${hotelSlug}#hotelreviewsection`,
          type: "",
          isActive: false,
          linkType: "internal",
        });
      }
    }
  }

  return navLinks;
};

/**
 * Resolves CMS custom tabs into the same INavLink shape used by createTabList.
 * Matches Art / WanWan / CRI header behaviour when isCustomTabEnabled is true.
 */
export const createCustomTabsNavLinks = (
  customTabsData: ContentJapanHotelTab[],
  hotelSlug: string,
  locale: string,
  isAttractionTemplate?: boolean,
): INavLink[] => {
  const links: INavLink[] = [];

  for (const tab of customTabsData) {
    const tabData = tab.tab_data;
    const title =
      tabData?.label || tab.item_name || tab.display_name || "";
    const itemName = tab.item_name || "";
    if (!title.trim()) continue;

    if (tabData?.use_custom_link && tabData.custom_link) {
      const href = generateLinkFromGeneralLink(
        tabData.custom_link as LinkConfig,
        locale,
      );
      const target = generateTargetFromLink(
        tabData.custom_link as LinkConfig,
      );
      const localePrefix = `/${locale}/`;
      const isLocalePrefixed = href.startsWith(localePrefix);
      links.push({
        title,
        item_name: itemName,
        url: isLocalePrefixed ? href.slice(localePrefix.length) : href,
        type: "",
        isActive: false,
        linkType:
          target === "_blank" || !isLocalePrefixed ? "external" : "internal",
      });
      continue;
    }

    if (tabData?.use_existing_tab) {
      if (tabData.use_existing_tab === "attractions") {
        links.push({
          title,
          item_name: itemName,
          url: isAttractionTemplate
            ? `attraction-${hotelSlug}`
            : `location-${hotelSlug}#attractions`,
          type: isAttractionTemplate ? "" : "anchor",
          isActive: false,
          linkType: "internal",
        });
        continue;
      }
      const prefix = EXISTING_TAB_URL_MAP[tabData.use_existing_tab] ?? "";
      links.push({
        title,
        item_name: itemName,
        url: `${prefix}${hotelSlug}`,
        type: "",
        isActive: false,
        linkType: "internal",
      });
    }
  }

  return links;
};

/**
 * Builds nav links the same way Art / WanWan / CRI headers do:
 * 1. Custom CMS tabs when enabled
 * 2. Otherwise standard createTabList (all hotel tabs with content)
 */
export const buildHeaderNavLinks = ({
  hotelTab,
  dataFilterTab,
  pathName,
  hotelSlug,
  locale,
  isAttractionTemplate,
  isCustomTabEnabled,
  customTabsData,
}: {
  hotelTab: GetHotelTabQuery | null | undefined;
  dataFilterTab: GetHotelDataFilterTabQuery | null | undefined;
  pathName: string;
  hotelSlug: string;
  locale: string;
  isAttractionTemplate?: boolean;
  isCustomTabEnabled?: boolean;
  customTabsData?: ContentJapanHotelTab[] | null;
}): INavLink[] | null => {
  if (
    isCustomTabEnabled &&
    customTabsData &&
    customTabsData.length > 0
  ) {
    const customLinks = createCustomTabsNavLinks(
      customTabsData,
      hotelSlug,
      locale,
      isAttractionTemplate,
    );
    return customLinks.length > 0 ? customLinks : null;
  }

  const tabDatasources = ((hotelTab?.sharedDatasources || []).find(
    (t) => !!t?.hotel,
  )?.hotel?.tab_datasources || []) as TabDatasource[];

  const hotelData = dataFilterTab?.japanHotels?.[0] as JapanHotel | undefined;

  if (!hotelData || tabDatasources.length === 0 || !pathName) {
    return null;
  }

  return createTabList(
    tabDatasources,
    hotelData,
    pathName,
    "",
    hotelSlug,
    locale,
    null,
    isAttractionTemplate,
  );
};
